import { useQueryOptionsFactory } from "@/queries";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type ToOptions, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useMemo, useState } from "react";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { auctionId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;
    const [open, setOpen] = useState(true);
    const router = useRouter();
    const navigate = useNavigate();

    const registrationUrl = useMemo(() => {
        const location = router.buildLocation({
            to: "/registration/$auctionId",
            params: { auctionId: auctionId },
        } satisfies ToOptions);
        return `${window.location.origin}${location.href}`;
    }, [router.buildLocation, auctionId]);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(registrationUrl);
            enqueueSnackbar("Link copied to clipboard", { variant: "success" });
        } catch {
            enqueueSnackbar("Failed to copy link to clipboard", { variant: "error" });
        }
    }, [registrationUrl]);

    return (
        <Dialog
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            maxWidth="sm"
            fullWidth
            slotProps={{
                transition: {
                    onExited: () => {
                        void navigate({
                            to: "/manage/auctions",
                            search: true,
                        });
                    },
                },
            }}
        >
            <DialogTitle>Public Registration Link</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ mb: 2 }}>
                    This is the public registration link your bidders can use to register for the
                    &quot;{auction.title}&quot; auction.
                </Typography>

                <TextField
                    value={registrationUrl}
                    slotProps={{
                        input: {
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Copy link">
                                        <IconButton edge="end" onClick={handleCopy}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        },
                    }}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setOpen(false);
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const Route = createFileRoute("/manage/auctions/$auctionId/registration-url")({
    component: Root,
});
